import React, { useState, useEffect, useCallback, useId, useMemo } from 'react';
import { TableSkeleton } from '../Skeleton';
import { adminService, AlwaysBlockedExtension, BlockedExtension } from '../../services/adminService';
import { RefreshCw, Plus, Trash2, Ban, AlertCircle, Lock, Search } from 'lucide-react';
import { ConfirmDialog } from '../ConfirmDialog';
import { useToast } from '../../contexts/ToastContext';
import { ADMIN_TAB_FILL, ADMIN_LIST_FILL } from './adminLayout';

const EXT_REGEX = /^\.[a-z0-9]{1,15}$/i;

/** 검색어 정규화 — 소문자, 앞뒤 공백·선행 점 무시 (".ht"와 "ht"가 같게 동작) */
const normalizeQuery = (q: string) => q.trim().toLowerCase().replace(/^\.+/, '');

const matches = (query: string, extension: string, description: string | null) => {
  if (!query) return true;
  return (
    extension.toLowerCase().includes(query) ||
    (description || '').toLowerCase().includes(query)
  );
};

/** 차단 확장자 한 줄. 관리 목록과 절대 차단 목록이 같은 모양을 공유한다 */
const ExtensionRow: React.FC<{
  extension: string;
  subtitle: React.ReactNode;
  badge?: React.ReactNode;
  locked?: boolean;
  onRemove?: () => void;
}> = ({ extension, subtitle, badge, locked, onRemove }) => (
  <div className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-colors">
    <div className="flex items-center gap-3 min-w-0">
      <div
        className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
          locked ? 'bg-slate-100' : 'bg-brand-50'
        }`}
      >
        {locked ? <Lock size={16} className="text-slate-500" /> : <Ban size={16} className="text-brand-600" />}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-mono font-semibold text-slate-800">{extension}</span>
          {badge}
        </div>
        <div className="text-xs text-slate-400 truncate">{subtitle}</div>
      </div>
    </div>
    {onRemove ? (
      <button
        onClick={onRemove}
        className="p-2 rounded-lg transition-colors shrink-0 text-slate-400 hover:text-brand-600 hover:bg-brand-50"
        title="차단 해제"
      >
        <Trash2 size={14} />
      </button>
    ) : null}
  </div>
);

export const BlockedExtensionsManager: React.FC = () => {
  const { showToast } = useToast();
  const fid = useId();
  const [removeTarget, setRemoveTarget] = useState<string | null>(null);
  const [items, setItems] = useState<BlockedExtension[]>([]);
  // 코드 상수라 화면에서 바꿀 수 없지만, 여기에 없으면 "목록에 없는데 첨부가 막힌다"가 된다
  const [alwaysBlocked, setAlwaysBlocked] = useState<AlwaysBlockedExtension[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [newExt, setNewExt] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // 절대 차단 목록은 없어도 관리 목록은 쓸 수 있어야 한다(서버가 아직 구버전인 배포 중간 상태).
      const [data, always] = await Promise.all([
        adminService.getBlockedExtensions(),
        adminService.getAlwaysBlockedExtensions().catch(err => {
          console.error('Failed to load always-blocked extensions:', err);
          return [] as AlwaysBlockedExtension[];
        }),
      ]);
      // 확장자 알파벳순 정렬
      setItems([...data].sort((a, b) => a.extension.localeCompare(b.extension)));
      setAlwaysBlocked([...always].sort((a, b) => a.extension.localeCompare(b.extension)));
    } catch (err) {
      console.error('Failed to load blocked extensions:', err);
      setLoadError(err instanceof Error ? err.message : '목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const q = normalizeQuery(query);
  const filteredItems = useMemo(
    () => items.filter(item => matches(q, item.extension, item.description)),
    [items, q]
  );
  const filteredAlways = useMemo(
    () => alwaysBlocked.filter(item => matches(q, item.extension, item.description)),
    [alwaysBlocked, q]
  );
  const totalCount = items.length + alwaysBlocked.length;
  const filteredCount = filteredItems.length + filteredAlways.length;

  const resetForm = () => {
    setShowForm(false);
    setNewExt('');
    setNewDesc('');
    setFormError(null);
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const ext = newExt.trim().toLowerCase();
    if (!EXT_REGEX.test(ext)) {
      setFormError('확장자는 점(.)으로 시작하고 영문/숫자 1~15자여야 합니다. 예: .iso');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      await adminService.addBlockedExtension(ext, newDesc.trim() || undefined);
      resetForm();
      await load();
    } catch (err) {
      console.error('Failed to add blocked extension:', err);
      setFormError(err instanceof Error ? err.message : '추가하지 못했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = (ext: string) => setRemoveTarget(ext);

  const performRemove = async () => {
    if (!removeTarget) return;
    const ext = removeTarget;
    setRemoveTarget(null);
    try {
      await adminService.removeBlockedExtension(ext);
      await load();
      showToast({ type: 'success', message: `${ext} 차단을 해제했습니다.` });
    } catch (err) {
      console.error('Failed to remove blocked extension:', err);
      showToast({ type: 'error', message: err instanceof Error ? err.message : '차단을 해제하지 못했습니다.' });
    }
  };

  return (
    <div className={ADMIN_TAB_FILL}>
      {/* 안내 배너 */}
      <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
        <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />
        <div className="text-xs text-amber-800">
          <p className="font-medium">첨부 차단 정책</p>
          <p className="mt-0.5">아래 두 목록에 있는 확장자는 채팅에 첨부할 수 없습니다. 변경 사항은 즉시 반영되며 감사 로그에 기록됩니다.</p>
          <p className="mt-0.5">
            <b>항상 차단</b> 목록은 악성코드·XSS 위험이 커서 해제할 수 없습니다.
          </p>
        </div>
      </div>

      {/* Actions bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="차단 확장자 검색"
            placeholder="확장자나 사유로 찾기"
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-brand-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            새로고침
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            <Plus size={14} />
            확장자 추가
          </button>
        </div>
      </div>

      <div className="mb-4 text-sm text-slate-500">
        {q
          ? `검색 결과 ${filteredCount}개 (전체 ${totalCount}개)`
          : `차단 확장자 ${totalCount}개 · 관리 ${items.length}개 · 항상 차단 ${alwaysBlocked.length}개`}
      </div>

      {/* Add form */}
      {showForm && (
        <form
          onSubmit={handleAdd}
          className="mb-4 p-4 bg-slate-50 rounded-xl border border-slate-200"
        >
          <h3 className="text-sm font-semibold text-slate-700 mb-3">차단 확장자 추가</h3>
          <div className="space-y-3">
            <div>
              <label htmlFor={`${fid}-ext`} className="block text-xs text-slate-500 mb-1">확장자 * (점 포함, 예: .iso)</label>
              <input id={`${fid}-ext`}
                type="text"
                value={newExt}
                onChange={(e) => setNewExt(e.target.value)}
                placeholder=".iso"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent font-mono"
                autoFocus
              />
            </div>
            <div>
              <label htmlFor={`${fid}-reason`} className="block text-xs text-slate-500 mb-1">차단 사유 (선택)</label>
              <input id={`${fid}-reason`}
                type="text"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                placeholder="예: 디스크 이미지, 악성 페이로드 위험"
                maxLength={200}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              />
            </div>
            {formError && (
              <div className="text-xs text-brand-600 flex items-start gap-1">
                <AlertCircle size={12} className="shrink-0 mt-0.5" />
                <span>{formError}</span>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={submitting || !newExt.trim()}
                className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
              >
                {submitting ? '추가 중...' : '추가'}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* List */}
      {loadError && (
        <div className="text-center py-12 text-brand-600 text-sm">{loadError}</div>
      )}
      {!loadError && loading && totalCount === 0 ? (
        <TableSkeleton rows={5} cols={3} />
      ) : !loadError && totalCount === 0 ? (
        <div className="text-center py-12 text-slate-400">차단된 확장자가 없습니다</div>
      ) : !loadError && filteredCount === 0 ? (
        <div className="text-center py-12">
          <p className="text-slate-500 text-sm">검색 결과가 없습니다</p>
          <p className="text-slate-400 text-xs mt-1">다른 검색어로 찾아보세요</p>
        </div>
      ) : !loadError && (
        <div className={`space-y-5 ${ADMIN_LIST_FILL}`}>
          {filteredItems.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold text-slate-500 mb-2">
                관리 차단 {filteredItems.length}개
              </h3>
              <div className="space-y-2">
                {filteredItems.map(item => (
                  <ExtensionRow
                    key={item.extension}
                    extension={item.extension}
                    badge={item.createdBy === 'system' ? (
                      <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-slate-100 text-slate-600">
                        기본
                      </span>
                    ) : undefined}
                    subtitle={
                      <>
                        {item.description && (
                          <>
                            <span>{item.description}</span>
                            <span className="mx-1">·</span>
                          </>
                        )}
                        추가자: {item.createdByName}
                        <span className="mx-1">·</span>
                        {new Date(item.createdAt).toLocaleString('ko-KR')}
                      </>
                    }
                    onRemove={() => handleRemove(item.extension)}
                  />
                ))}
              </div>
            </section>
          )}

          {filteredAlways.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold text-slate-500 mb-2">
                항상 차단 {filteredAlways.length}개
              </h3>
              <div className="space-y-2">
                {filteredAlways.map(item => (
                  <ExtensionRow
                    key={item.extension}
                    extension={item.extension}
                    locked
                    badge={
                      <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-brand-50 text-brand-700 border border-brand-200">
                        해제 불가
                      </span>
                    }
                    subtitle={item.description}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!removeTarget}
        title="차단 해제"
        message={`${removeTarget ?? ''} 차단을 해제할까요? 해당 확장자 파일을 다시 첨부할 수 있게 됩니다.`}
        confirmLabel="해제"
        danger
        onConfirm={performRemove}
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
};
